import * as React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { RouteComponentProps, withRouter } from 'react-router'

import classnames from 'classnames'
import { Button } from 'reactstrap'
import ScrollBar from 'react-perfect-scrollbar'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { Employee } from '../../../lib/interfaces/Employee.interface'
import { Termination } from '../../../lib/interfaces/Termination.interface'
import { User, hasAnyPermission } from '../../../lib/interfaces/User.interface'

import { formatDate } from '../../../lib/helpers/time'
import { formatName } from '../../../lib/helpers/format'

import FileList from 'src/common/FileList.component'
import Collapsible from '../../../common/Collapsible.component'
import LocationImage from '../../../common/LocationImage.component'

import TerminationFlow from '../../terminations/termination-flow'
import TerminationFlowStatus from '../../terminations/termination-flow/TerminationFlowStatus.type'
import TerminationFormData from '../../terminations/termination-flow/TerminationFormData.interface'

import * as employeesActions from '../../../store/employees/actions'
import * as terminationsActions from '../../../store/terminations/actions'

const get = require('lodash/get')

export interface CurrentEmployeeProps extends RouteComponentProps {
  user: User
  employee: Employee
  isFetchingFiles: boolean
  resetEmployee: () => void
  isCreatingTermination: boolean
  fetchEmployeeFiles: (employeeId: string) => void
  createTermination: (termination: TerminationFormData, next?: (err?: any, termination?: Termination) => void) => void
}

export interface CurrentEmployeeState {
  showQRContent: boolean
  collapsedContact: boolean
  terminationForm?: TerminationFormData
  terminationState: TerminationFlowStatus
}

class CurrentEmployee extends React.Component<CurrentEmployeeProps, CurrentEmployeeState> {
  state: CurrentEmployeeState = {
    showQRContent: false,
    collapsedContact: true,
    terminationState: null,
    terminationForm: undefined
  };

  constructor(props: CurrentEmployeeProps) {
    super(props);

    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    this.startTerminationFlow = this.startTerminationFlow.bind(this);
  }

  componentDidMount() {
    this.fetchFiles();

    document.addEventListener("keyup", this.handleEscapeKey);
  }

  componentDidUpdate(prevProps) {
    const { employee } = this.props;

    if (employee.id !== prevProps.employee.id) {
      this.fetchFiles();
    }
  }

  componentWillUnmount() {
    document.removeEventListener("keyup", this.handleEscapeKey);
  }

  fetchFiles() {
    const { employee, fetchEmployeeFiles } = this.props;

    fetchEmployeeFiles(employee.id);
  }

  handleEscapeKey(e) {
    if (e.keyCode === 27) {
      this.handleResetCurrentEmployee(e)
    }
  }

  handleResetCurrentEmployee = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    this.props.resetEmployee()
  };

  toggleQRContent() {
    this.setState({ showQRContent: !this.state.showQRContent })
  }

  startTerminationFlow(
    terminationState: TerminationFlowStatus,
    terminationForm?: TerminationFormData
  ) {
    if (terminationState === 'SUBMITTING') {
      this.setState({ terminationState: null, terminationForm: undefined }, () => {
        this.props.createTermination(terminationForm!, (err?: any, termination?: Termination) => {
          if (termination) {
            this.props.resetEmployee()
            this.props.history.push(`/terminations/${termination.id}`)
          }
        })
      })

      return
    }
    else if (terminationState === 'INITIAL_CONFIRMATION') {
      terminationForm = {
        sendSurvey: true,
        reason: undefined,
        comments: undefined,
        voluntary: undefined,
        lastDayOfWork: undefined,
        employee: this.props.employee
      };
    }

    this.setState(() => ({ terminationState, terminationForm }));
  }

  getNameText() {
    if (this.props.employee.firstName) {
      return formatName(this.props.employee);
    }
    return 'Name not available'
  }

  getLocationText() {
    const { employee } = this.props

    if (employee.location && employee.location!.code) {
      return `Location: ${employee.location!.code}`
    }

    return 'Location not available'
  }

  get filesCollapsibleTitle() {
    const { employee } = this.props

    if (employee.files) {
      return `Files (${employee.files.length || 0})`
    }

    return 'Files'
  }

  render() {
    const { terminationForm, showQRContent } = this.state
    const { user, employee, isFetchingFiles, isCreatingTermination } = this.props

    return (
      <div className="col-md-4 sidedetails">
        <div className="d-flex flex-column h-100 p-4">
          <div className="d-flex justify-content-between align-items-center">
            <h4>{this.getNameText()}</h4>
            <a
              href=""
              className="sidedetails-close mx-2"
              onClick={this.handleResetCurrentEmployee}
            >
              <FontAwesomeIcon icon={["fas", "times-circle"]} />
            </a>
          </div>
          <p>{this.getLocationText()}</p>

          <ScrollBar className={classnames('flex-grow-1 min-height-0', { 'd-flex align-items-center justify-content-center': !!this.state.terminationState })}>
            {this.state.terminationState
              ? (
                <TerminationFlow
                  terminationForm={terminationForm!}
                  statusCallback={this.startTerminationFlow}
                />
              )
              : (
                <div>
                  <Collapsible
                    defaultOpen
                    icon={["far", "user"]}
                    title="Employee Status"
                  >
                    <table className="w-100">
                      <tbody>
                        <tr>
                          <th scope="col" className="text-uppercase py-2">
                            Employee ID
                          </th>
                          <td>{employee.id}</td>
                        </tr>
                        <tr>
                          <th scope="col" className="text-uppercase py-2">
                            Status
                          </th>
                          <td>{employee.employmentStatus || "None"}</td>
                        </tr>
                        <tr>
                          <th scope="col" className="text-uppercase py-2">
                            Hire Date
                          </th>
                          <td>{formatDate(employee.hireDate)}</td>
                        </tr>
                        <tr>
                          <th scope="col" className="text-uppercase py-2">
                            Location
                          </th>
                          <td>{employee.location && employee.location!.name}</td>
                        </tr>
                      </tbody>
                    </table>

                    {employee.location && (
                      <LocationImage
                        style={{ height: "170px" }}
                        locationCode={employee.location.code}
                      />
                    )}
                  </Collapsible>

                  <Collapsible
                    title={this.filesCollapsibleTitle}
                    icon={["far", "save"]}
                    isLoading={isFetchingFiles}
                  >
                    <FileList
                      viewMode="card"
                      entity="employee"
                      files={employee.files}
                      entityId={employee.id}
                      fileWrapperClassName="list-group-item"
                      className="gglist list-group list-group-flush"
                    />
                  </Collapsible>

                  {/* <Collapsible icon={['far', 'folder-open']} title="Comments About Applicant">
                    <EmployeeCommentList />
                  </Collapsible> */}

                  {employee.jobApplicant && (
                    <Collapsible
                      icon={["far", "list-alt"]}
                      title="Applicant Employment History"
                    >
                      Hello World
                    </Collapsible>
                  )}

                  {employee.jobApplicant && (
                    <Collapsible
                      icon={["far", "newspaper"]}
                      title="Applicant Education"
                    >
                      Hello World
                    </Collapsible>
                  )}

                  {employee.jobApplicant && (
                    <Collapsible
                      icon={["far", "plus-square"]}
                      title="Add Applicant Files"
                    >
                      Hello World
                    </Collapsible>
                  )}

                  {showQRContent && (
                    <span>
                      {JSON.stringify({
                        id: employee.id,
                        f: employee.firstName,
                        l: employee.lastName,
                        h: employee.hireDate
                      })}
                    </span>
                  )}

                  {hasAnyPermission(user, ['ROLE_OFFICE_TERMINATE', 'ROLE_LOCATION_TERMINATE']) && get(employee, 'terminations.length', 0) === 0 &&
                    <button disabled={isCreatingTermination} className="btn btn-primary btn-block" onClick={e => this.startTerminationFlow('INITIAL_CONFIRMATION')}>
                      {isCreatingTermination && <FontAwesomeIcon spin icon={["fas", "spinner"]} className="mr-3" />}
                      Terminate Employee
                    </button>
                  }

                  <button
                    className="btn btn-primary btn-block"
                    onClick={e => this.toggleQRContent()}
                  >
                    Show QR Content
                  </button>
                </div>
              )
            }
          </ScrollBar>

          {!this.state.terminationState && (
            <Link className="mt-2" to={`/employees/${employee.id}`}>
              <Button color="primary" className="w-100 mt-1 py-2">
                Details page
              </Button>
            </Link>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ auth, employees, terminations }) => ({
  user: auth.user,
  employee: employees.currentEmployee,
  isFetchingFiles: employees.isFetchingFiles,
  isCreatingTermination: terminations.isCreating
});

const mapDispatchToProps = dispatch => ({
  resetEmployee: () => dispatch(employeesActions.setCurrentEmployee(undefined)),
  fetchEmployeeFiles: (employeeId: string) =>
    dispatch(employeesActions.fetchEmployeeFiles(employeeId)),
  createTermination: (termination: TerminationFormData, next?: (err?: any, termination?: Termination) => void) =>
    dispatch(terminationsActions.createTermination(termination, next))
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(CurrentEmployee));



// WEBPACK FOOTER //
// ./src/routes/employees/components/CurrentEmployee.component.tsx